/**
 * Intake BE(spnp) `/v1/datasets/{dataset_uuid}/facts` 경계 — Dataset 조사 사실의 active · candidate · 이력.
 *
 * **칸의 `null` 뜻은 이 경계가 소유한다**(규칙 §6). 칸은 `{value, basis, note}` 이고 셋은 함께 `null` 이거나 함께
 * 값이다.
 * - 셋 다 `null` — **자료 없음.** 아무도 조사하지 않았다. 화면 · MCP 는 "직접 조사" 로 안내한다. `0` · 빈 문자열 ·
 *   기본값으로 접지 않는다.
 * - 셋 다 값 — 조사한 사실이다. `basis` 는 근거의 종류, `note` 는 출처 · 조사 범위다.
 * - 일부만 `null` — 계약 위반이다. 서버는 그런 칸을 저장하지 않는다.
 *
 * **active 는 늘 온다.** 아직 아무도 쓰지 않았으면 모든 칸이 자료 없음이고 `revision` 은 0 이다. **candidate 는
 * `null` 이 제안 없음이다.** 키가 없으면 계약 위반이다.
 *
 * **문서는 키가 정확히 맞아야 한다.** 묶음 넷 · 칸 열하나 · 칸마다 세 키다. 모르는 키나 빠진 키는 계약 위반이다
 * (BE 가 요청에서 `extra=forbid` 로 거부하는 것과 같다). 그래서 BE 가 칸을 더하면 새 칸을 아는 FE 를 먼저 배포한다.
 * 칸 이름은 wire 경로 그대로라 문서는 변환 없이 오가고, 응답 껍데기만 camelCase 로 옮긴다.
 *
 * **`client` 는 기록용 힌트다.** 쓰기를 보낸 곳(`X-Spnp-Client`)이고 권한이 아니다. 모르는 문자열은 계약 위반으로
 * 보지 않고 `unknown` 으로 읽는다 — BE 가 보낸 곳을 더해도 그 이력 · 제안 전체를 못 읽게 되면 안 된다. 문자열이
 * 아니면 계약 위반이다.
 *
 * **쓰기는 `expected_revision` 으로 잠근다.** 다르면 409 `dataset_facts.revision_conflict` 다. 이 경계는 그 오류를
 * 그대로 던진다. 다시 받아 서버를 따르는 일은 `feature/datasetFactsEditor.ts` 가 한다.
 *
 * Owns:
 * - 칸 `null` 의 뜻, 모르는 `client` 를 `unknown` 으로 읽기, 문서의 키 검사와 값 읽기, 요청 본문 구성, 응답 · 이력 읽기.
 *
 * Does not own:
 * - 유효성 규칙 — `domain/datasetFacts.ts` 의 `factIssues`(서버 판정의 사본). 409 따라가기 · 편집 상태 —
 *   `feature/datasetFactsEditor.ts`. `X-Spnp-Client: fe` 헤더 — `apiClient`. 오류 → 문구 —
 *   `feature/datasetFactsFormat.ts`.
 */
import {
  apiClient,
  IntakeContractError,
  readBoundedString,
  readFiniteNumber,
  readInstantMs,
  readInteger,
  readList,
  readRecord,
  readString,
} from "@/shared/http";
import {
  AXES,
  DEFECT_STATES,
  FACT_BASES,
  FACT_CLIENTS,
  FACT_EVENT_ACTIONS,
  FACT_GROUPS,
  FACT_PATHS,
  factFieldOf,
  factGroupOf,
  LENGTH_UNITS,
  ORIGIN_POINTS,
  QUATERNION_ORDERS,
  type DatasetFacts,
  type DatasetFactsRecord,
  type Fact,
  type FactClient,
  type FactGroup,
  type FactPath,
  type FactsCandidate,
  type FactsEvent,
  type FactsSnapshot,
} from "../domain/datasetFacts";

const RESOURCE = "dataset_facts";
const FACT_KEYS = ["value", "basis", "note"] as const;

/** 쓰기의 모양. `expectedRevision` 은 마지막으로 읽은 `revision` 이다. */
export type FactsWrite = {
  readonly expectedRevision: number;
  readonly facts: DatasetFacts;
};

export async function getDatasetFacts(
  datasetUuid: string,
  signal?: AbortSignal,
): Promise<DatasetFactsRecord> {
  const response = await apiClient.get<unknown>(factsPath(datasetUuid), { signal });
  return parseFactsRecord(response.data);
}

/** 제안 — active 는 그대로 두고 candidate 를 통째로 바꾼다. 마지막 제안이 이긴다. */
export async function proposeDatasetFacts(
  datasetUuid: string,
  write: FactsWrite,
): Promise<DatasetFactsRecord> {
  const response = await apiClient.put<unknown>(
    `${factsPath(datasetUuid)}/candidate`,
    writeBody(write),
  );
  return parseFactsRecord(response.data);
}

/** 확정 — 본문이 active 가 되고 candidate 가 있으면 지운다. 저장된 candidate 가 아니라 본문을 올린다. */
export async function confirmDatasetFacts(
  datasetUuid: string,
  write: FactsWrite,
): Promise<DatasetFactsRecord> {
  const response = await apiClient.put<unknown>(factsPath(datasetUuid), writeBody(write));
  return parseFactsRecord(response.data);
}

/** 제안 폐기 — 남이 막 올린 제안을 모르고 지우지 않게 revision 을 함께 보낸다. */
export async function discardDatasetFactsCandidate(
  datasetUuid: string,
  expectedRevision: number,
): Promise<DatasetFactsRecord> {
  const response = await apiClient.delete<unknown>(`${factsPath(datasetUuid)}/candidate`, {
    params: { expected_revision: expectedRevision },
  });
  return parseFactsRecord(response.data);
}

/** 바뀐 이력. 최신 먼저다. `limit` 은 1–200 이다. */
export async function getDatasetFactsHistory(
  datasetUuid: string,
  limit = 50,
): Promise<FactsEvent[]> {
  const response = await apiClient.get<unknown>(`${factsPath(datasetUuid)}/history`, {
    params: { limit },
  });
  const body = readRecord(response.data, RESOURCE, "history");
  return readList(body.events, RESOURCE, "events").map((event, index) =>
    parseFactsEvent(event, `events.${index}`),
  );
}

function factsPath(datasetUuid: string): string {
  return `/v1/datasets/${datasetUuid}/facts`;
}

function writeBody(write: FactsWrite): Record<string, unknown> {
  return { expected_revision: write.expectedRevision, facts: factsToWire(write.facts) };
}

/** 사실 문서 → wire. 묶음별로 다시 접는다. */
export function factsToWire(facts: DatasetFacts): Record<FactGroup, Record<string, unknown>> {
  const wire = Object.fromEntries(FACT_GROUPS.map((group) => [group, {}])) as Record<
    FactGroup,
    Record<string, unknown>
  >;
  for (const path of FACT_PATHS) {
    const fact = facts[path];
    wire[factGroupOf(path)][factFieldOf(path)] = {
      value: fact.value,
      basis: fact.basis,
      note: fact.note,
    };
  }
  return wire;
}

export function parseFactsRecord(value: unknown): DatasetFactsRecord {
  const body = readRecord(value, RESOURCE, "body");
  return {
    datasetUuid: readString(body.dataset_uuid, RESOURCE, "dataset_uuid"),
    revision: readRevision(body.revision, "revision"),
    active: parseFacts(body.active, "active"),
    candidate: parseCandidate(body.candidate),
  };
}

function parseCandidate(value: unknown): FactsCandidate | null {
  if (value === null) {
    return null;
  }
  const row = readRecord(value, RESOURCE, "candidate");
  return {
    facts: parseFacts(row.facts, "candidate.facts"),
    proposedBy: readString(row.proposed_by, RESOURCE, "candidate.proposed_by"),
    client: readClient(row.client, "candidate.client"),
    modifiedAtMs: readInstantMs(row.modified_at, RESOURCE, "candidate.modified_at"),
  };
}

function parseFactsEvent(value: unknown, field: string): FactsEvent {
  const row = readRecord(value, RESOURCE, field);
  return {
    uuid: readString(row.uuid, RESOURCE, `${field}.uuid`),
    action: readBoundedString(row.action, FACT_EVENT_ACTIONS, RESOURCE, `${field}.action`),
    actorMemberUuid: readString(row.actor_member_uuid, RESOURCE, `${field}.actor_member_uuid`),
    client: readClient(row.client, `${field}.client`),
    revision: readRevision(row.revision, `${field}.revision`),
    before: parseSnapshot(row.before, `${field}.before`),
    after: parseSnapshot(row.after, `${field}.after`),
    createdAtMs: readInstantMs(row.created_at, RESOURCE, `${field}.created_at`),
  };
}

function parseSnapshot(value: unknown, field: string): FactsSnapshot {
  const row = readRecord(value, RESOURCE, field);
  return {
    active: parseFacts(row.active, `${field}.active`),
    candidate: row.candidate === null ? null : parseFacts(row.candidate, `${field}.candidate`),
  };
}

/** 보낸 곳. 모르는 문자열은 `unknown` 이다(머리 주석). */
function readClient(value: unknown, field: string): FactClient {
  const client = readString(value, RESOURCE, field);
  return FACT_CLIENTS.find((known) => known === client) ?? "unknown";
}

function readRevision(value: unknown, field: string): number {
  const revision = readInteger(value, RESOURCE, field);
  if (revision < 0) {
    throw new IntakeContractError(RESOURCE, field);
  }
  return revision;
}

/** wire 문서 → 사실 문서. 키가 정확히 맞아야 하고 칸마다 값 타입을 본다. */
export function parseFacts(value: unknown, field: string): DatasetFacts {
  const body = readRecord(value, RESOURCE, field);
  requireExactKeys(body, FACT_GROUPS, field);
  const groups = Object.fromEntries(
    FACT_GROUPS.map((group) => {
      const row = readRecord(body[group], RESOURCE, `${field}.${group}`);
      const fields = FACT_PATHS.filter((path) => factGroupOf(path) === group).map(factFieldOf);
      requireExactKeys(row, fields, `${field}.${group}`);
      return [group, row];
    }),
  ) as Record<FactGroup, Record<string, unknown>>;
  const raw = (path: FactPath): unknown => groups[factGroupOf(path)][factFieldOf(path)];
  const at = (path: FactPath): string => `${field}.${path}`;
  const choice =
    <T extends string>(options: readonly T[]) =>
    (value: unknown, name: string): T =>
      readBoundedString(value, options, RESOURCE, name);
  const text = (value: unknown, name: string): string => readString(value, RESOURCE, name);
  const number = (value: unknown, name: string): number => readFiniteNumber(value, RESOURCE, name);

  return {
    "end_pose.point": parseFact(raw("end_pose.point"), choice(ORIGIN_POINTS), at("end_pose.point")),
    "end_pose.frame": parseFact(raw("end_pose.frame"), text, at("end_pose.frame")),
    "end_pose.quaternion_order": parseFact(
      raw("end_pose.quaternion_order"),
      choice(QUATERNION_ORDERS),
      at("end_pose.quaternion_order"),
    ),
    "end_pose.position_unit": parseFact(
      raw("end_pose.position_unit"),
      choice(LENGTH_UNITS),
      at("end_pose.position_unit"),
    ),
    "hand_axes.approach": parseFact(
      raw("hand_axes.approach"),
      choice(AXES),
      at("hand_axes.approach"),
    ),
    "hand_axes.up": parseFact(raw("hand_axes.up"), choice(AXES), at("hand_axes.up")),
    "gripper.channel": parseFact(raw("gripper.channel"), text, at("gripper.channel")),
    "gripper.open_value": parseFact(raw("gripper.open_value"), number, at("gripper.open_value")),
    "gripper.closed_value": parseFact(
      raw("gripper.closed_value"),
      number,
      at("gripper.closed_value"),
    ),
    "defects.quaternion_norm": parseFact(
      raw("defects.quaternion_norm"),
      choice(DEFECT_STATES),
      at("defects.quaternion_norm"),
    ),
    "defects.channel_names": parseFact(
      raw("defects.channel_names"),
      choice(DEFECT_STATES),
      at("defects.channel_names"),
    ),
  };
}

/** 칸 하나. 셋 다 `null` 이면 자료 없음이고, 하나라도 값이면 셋 다 값이어야 한다. */
function parseFact<T>(
  value: unknown,
  readValue: (value: unknown, field: string) => T,
  field: string,
): Fact<T> {
  const row = readRecord(value, RESOURCE, field);
  requireExactKeys(row, FACT_KEYS, field);
  if (row.value === null && row.basis === null && row.note === null) {
    return { value: null, basis: null, note: null };
  }
  return {
    value: readValue(row.value, `${field}.value`),
    basis: readBoundedString(row.basis, FACT_BASES, RESOURCE, `${field}.basis`),
    note: readString(row.note, RESOURCE, `${field}.note`),
  };
}

function requireExactKeys(
  row: Record<string, unknown>,
  keys: readonly string[],
  field: string,
): void {
  for (const key of keys) {
    if (!(key in row)) {
      throw new IntakeContractError(RESOURCE, `${field}.${key}`);
    }
  }
  for (const key of Object.keys(row)) {
    if (!keys.includes(key)) {
      throw new IntakeContractError(RESOURCE, `${field}.${key}`);
    }
  }
}
